/**
 * SCR-026 — Portal Grades /portal/grades
 * Grade table grouped by class, score/100, color chip by range.
 * Mobile: card per grade. md+: full table.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePortalGrades } from '@client/lib/queries/use-portal';
import { useChildSelector } from '@client/store/child-selector';
import { authClient } from '@client/lib/auth-client';

export const Route = createFileRoute('/portal/grades')({
  component: PortalGrades,
});

const GRADE_TYPE_LABEL: Record<string, string> = {
  quiz: 'Kiểm tra', midterm: 'Giữa kỳ', final: 'Cuối kỳ',
  participation: 'Tham gia', assignment: 'Bài tập',
};

function scoreChipClass(score: number, maxScore: number) {
  const pct = (score / maxScore) * 100;
  if (pct >= 80) return 'bg-brand-green-100 text-brand-green-700 dark:bg-brand-green-900/50 dark:text-brand-green-300';
  if (pct >= 50) return 'bg-brand-orange-100 text-brand-orange-700 dark:bg-brand-orange-950/50 dark:text-brand-orange-300';
  return 'bg-destructive/10 text-destructive';
}
function scoreChipLabel(score: number, maxScore: number) {
  const pct = (score / maxScore) * 100;
  if (pct >= 80) return 'Tốt';
  if (pct >= 50) return 'Trung bình';
  return 'Yếu';
}

function PortalGrades() {
  const { data: session } = authClient.useSession();
  const role = (session?.user as { role?: string })?.role ?? '';
  const { selectedChildId } = useChildSelector();
  const studentId = role === 'parent' ? selectedChildId : undefined;

  const [classFilter, setClassFilter] = useState<string>('all');

  const { data, isLoading, isError, refetch } = usePortalGrades(studentId);

  if (isLoading) return <GradesSkeleton />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Không tải được bảng điểm</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />Thử lại
        </Button>
      </div>
    );
  }

  const classes = data?.classes ?? [];
  const filtered = classFilter === 'all' ? classes : classes.filter((c) => c.classId === classFilter);

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl font-bold">Bảng điểm</h1>
        {classes.length > 1 && (
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-full sm:w-52 h-9 text-sm">
              <SelectValue placeholder="Lọc theo lớp" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả lớp</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c.classId} value={c.classId}>{c.className}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {classes.length === 0 && (
        <div className="py-16 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
          Chưa có điểm — giáo viên chưa nhập điểm
        </div>
      )}

      {filtered.map((cls) => (
        <section key={cls.classId}>
          <Card className="border-border">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-base">{cls.className}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{cls.courseName}</p>
                </div>
                <div className="text-sm font-semibold text-right">
                  TB:{' '}
                  <span className="text-foreground">
                    {cls.averageScore != null ? `${cls.averageScore} / 100` : '—'}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {cls.grades.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Chưa có điểm</p>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-muted-foreground border-b">
                          <th className="text-left py-2 pr-4 font-medium">Loại điểm</th>
                          <th className="text-right py-2 pr-4 font-medium">Điểm</th>
                          <th className="text-right py-2 pr-4 font-medium">Thang</th>
                          <th className="text-left py-2 pr-4 font-medium">Xếp loại</th>
                          <th className="text-left py-2 font-medium">Nhận xét</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cls.grades.map((g) => (
                          <tr key={g.id} className="border-b last:border-0">
                            <td className="py-2 pr-4">
                              <Badge variant="outline" className="text-xs font-normal">
                                {GRADE_TYPE_LABEL[g.gradeType] ?? g.gradeType}
                              </Badge>
                            </td>
                            <td className="py-2 pr-4 text-right font-semibold">{g.score}</td>
                            <td className="py-2 pr-4 text-right text-muted-foreground">{g.maxScore}</td>
                            <td className="py-2 pr-4">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${scoreChipClass(g.score, g.maxScore)}`}>
                                {scoreChipLabel(g.score, g.maxScore)}
                              </span>
                            </td>
                            <td className="py-2 text-xs text-muted-foreground max-w-[200px] truncate">
                              {g.feedback ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {cls.grades.map((g) => (
                      <div key={g.id} className="border border-border rounded-lg p-3 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="outline" className="text-xs font-normal">
                            {GRADE_TYPE_LABEL[g.gradeType] ?? g.gradeType}
                          </Badge>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${scoreChipClass(g.score, g.maxScore)}`}>
                            {scoreChipLabel(g.score, g.maxScore)}
                          </span>
                        </div>
                        <p className="text-xl font-bold">
                          {g.score}
                          <span className="text-sm text-muted-foreground font-normal ml-1">/ {g.maxScore}</span>
                        </p>
                        {g.feedback && (
                          <p className="text-xs text-muted-foreground">{g.feedback}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </section>
      ))}
    </div>
  );
}

function GradesSkeleton() {
  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <Skeleton className="h-7 w-36" />
      {[...Array(2)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      ))}
    </div>
  );
}
